import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import type { FollowUpQueueResponse, FollowUpState } from '@fitting-room/contracts'
import { AppointmentDrawer } from '../../components/admin/AppointmentDrawer'
import { DatePicker } from '../../components/admin/DatePicker'
import { PageHeader } from '../../components/admin/PageHeader'
import { StaffMark } from '../../components/admin/StaffMark'
import { useAppointmentTypes, useAssignees, useBackOffice, useCloseFollowUp, useFollowUps, useSetFollowUp } from '../../lib/adminQueries'
import { FOLLOW_UP_PRIORITY_LABEL, assigneeName, labelForKey } from '../../lib/appointments'
import { BOUTIQUE_LOCALE, boutiqueDayOf } from '../../lib/boutique'
import { formatDay } from '../../lib/format'
import { shiftDay } from '../../lib/months'
import { useStaffToken } from '../../lib/staffToken'
import { walkNeighbours } from './visitFilter'

type FollowUp = FollowUpQueueResponse['followUps'][number]

const GROUP_TITLE: Record<FollowUpState, string> = {
  overdue: 'Overdue',
  due: 'Today',
  upcoming: 'Coming up',
}

/**
 * Follow-ups: every appointment somebody means to get back to, in the order
 * to do it — overdue first, then by date, then by priority. The API orders
 * them (`orderFollowUps`); this page groups that order under three headings
 * and never re-sorts it.
 *
 * Each row does the three things the queue is for: mark it done, snooze it
 * (which is choosing a new date), or open the appointment to see the rest.
 * `docs/22-back-office.md` § "Appointments".
 */
export function FollowUpsPage() {
  const { token } = useStaffToken()
  const [params, setParams] = useSearchParams()
  const queue = useFollowUps(token)
  const openVisitId = params.get('visit')

  const followUps = useMemo(() => queue.data?.followUps ?? [], [queue.data])
  const groups = useMemo(() => {
    const out: Array<{ state: FollowUpState; rows: FollowUp[] }> = []
    for (const row of followUps) {
      const last = out[out.length - 1]
      if (last?.state === row.followUpState) last.rows.push(row)
      else out.push({ state: row.followUpState, rows: [row] })
    }
    return out
  }, [followUps])
  const { current, previous, next } = walkNeighbours(followUps, openVisitId)

  const open = (id: string | undefined) =>
    setParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev)
        if (id) nextParams.set('visit', id)
        else nextParams.delete('visit')
        return nextParams
      },
      { replace: true },
    )

  const overdue = followUps.filter((f) => f.followUpState === 'overdue').length

  return (
    <div>
      <PageHeader eyebrow="Getting back to customers" title="Follow-ups">
        {overdue > 0 ? <span className="st-badge st-badge--alert">{overdue} overdue</span> : null}
      </PageHeader>

      {queue.isError ? <p className="st-alert mb-4">Couldn&rsquo;t load the follow-ups. Try again in a moment.</p> : null}
      {queue.isPending ? <p className="st-meta">Loading…</p> : null}

      {!queue.isPending && followUps.length === 0 ? (
        <div className="st-card px-4 py-10 text-center">
          <p className="st-meta">Nobody to follow up with. Add a follow-up from an appointment when a customer needs one.</p>
        </div>
      ) : null}

      {groups.map((group) => (
        <section key={group.state} className="mb-6">
          <h2 className="st-eyebrow mb-2" style={group.state === 'overdue' ? { color: '#96432f' } : undefined}>
            {GROUP_TITLE[group.state]} <span className="tabular-nums">{group.rows.length}</span>
          </h2>
          {group.rows.map((row) => (
            <FollowUpRow key={row.id} row={row} today={queue.data?.today ?? ''} onOpen={() => open(row.id)} />
          ))}
        </section>
      ))}

      <AppointmentDrawer
        visitId={openVisitId}
        row={current}
        onClose={() => open(undefined)}
        onPrevious={previous ? () => open(previous.id) : undefined}
        onNext={next ? () => open(next.id) : undefined}
      />
    </div>
  )
}

function FollowUpRow({ row, today, onOpen }: { readonly row: FollowUp; readonly today: string; readonly onOpen: () => void }) {
  const { token } = useStaffToken()
  const members = useAssignees(token).data?.members ?? []
  const types = useAppointmentTypes(token).data?.types
  const settings = useBackOffice(token).data
  const close = useCloseFollowUp(token, row.id)
  const snooze = useSetFollowUp(token, row.id)
  const member = members.find((m) => m.id === row.assigneeId)
  const priority = row.followUpPriority ?? 'normal'
  const moveTo = (on: string) => snooze.mutate({ on, priority, ...(row.followUpNote ? { note: row.followUpNote } : {}) })
  const error = close.error ?? snooze.error

  return (
    <div className="st-card mb-2 px-4 py-3" data-visit-id={row.id} data-follow-up-state={row.followUpState}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button type="button" className="min-w-0 flex-1 text-left" onClick={onOpen}>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="st-display text-lg tabular-nums" style={row.followUpState === 'overdue' ? { color: '#96432f' } : undefined}>
              {formatDay(row.followUpOn as string, BOUTIQUE_LOCALE)}
            </span>
            <span className="font-medium underline-offset-2 hover:underline">{row.customerName ?? 'No name'}</span>
            {row.customerPhone ? <span className="st-meta tabular-nums">{row.customerPhone}</span> : null}
            <span className="st-badge st-priority" data-priority={priority}>
              {FOLLOW_UP_PRIORITY_LABEL[priority]}
            </span>
          </div>
          {row.followUpNote ? <p className="mt-1 whitespace-pre-wrap text-sm">{row.followUpNote}</p> : null}
          <div className="st-meta mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span>
              Seen {formatDay(boutiqueDayOf(row.scheduledAt), BOUTIQUE_LOCALE)} · {types?.find((t) => t.key === row.tierKey)?.label ?? row.tierKey}
            </span>
            {row.noPurchaseReason ? <span>{labelForKey(settings?.noPurchaseReasons, row.noPurchaseReason)}</span> : null}
            {row.assigneeId ? (
              <span className="flex items-center gap-1">
                <StaffMark mark={member?.calendarMark} name={member?.displayName} />
                {assigneeName(members, row.assigneeId)}
              </span>
            ) : null}
          </div>
        </button>

        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" className="st-btn" onClick={() => close.mutate('done')} disabled={close.isPending}>
            Done
          </button>
          <button type="button" className="st-btn-ghost" onClick={() => moveTo(shiftDay(today, 1))} disabled={snooze.isPending}>
            Tomorrow
          </button>
          <button type="button" className="st-btn-ghost" onClick={() => moveTo(shiftDay(today, 7))} disabled={snooze.isPending}>
            In a week
          </button>
          <DatePicker value={row.followUpOn as string} onChange={moveTo} label={`Snooze ${row.customerName ?? 'this follow-up'} to`} />
        </div>
      </div>
      {error ? <p className="mt-1 text-xs text-[color:#8d3f2c]">{error.message}</p> : null}
    </div>
  )
}

